import { Button, DropdownMenu, TableItem } from 'components/atoms';
import { FormInput, MagellanPopups } from 'components/molecules';
import LabeledSettlementOrderStatus from 'components/molecules/labeledStatus/LabeledSettlementOrderStatus';
import { QueryFilter, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { FormikHelpers } from 'formik';
import { useComponentVisible, useHasPermission } from 'hooks';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import i18next from 'i18next';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PiCheck, PiNavigationArrow, PiX, PiXCircle } from 'react-icons/pi';
import { PopupActions } from 'reactjs-popup/dist/types';
import { useCheckSettlementPeriods } from 'services/labels';
import { useGetLabelLightList } from 'services/labels/light';
import {
  useAcceptSettlementOrder,
  useCancelAllSettlementOrders,
  useCancelSettlementOrder,
  useCreateSettlementOrder,
  useGetSettlementOrderLabels,
  useGetSettlementOrderListInfinite,
  useRejectSettlementOrder,
} from 'services/settlementOrder';
import { SETTLEMENT_ORDER_STATUS } from 'services/settlementOrder/enums';
import {
  mapCreateSettlementOrderBody,
  mapCreateSettlementOrderLabelsBody,
  ISettlementOrderByLabelForm,
  ISettlementOrderLabelsForm,
} from 'services/settlementOrder/settlementOrder.utils';
import { ISettlementOrder } from 'services/settlementOrder/types';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatDate, formatDateToInput } from 'utils/global/date.utils';
import { getSettlementPeriodName } from 'utils/global/name.utils';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';
import { getNodes } from 'utils/table/nodes.utils';
import { useSearchById, useSetSearchById } from 'store/state';
import { boolean, object, string } from 'yup';
import { useQueryClient } from '@tanstack/react-query';

enum SORT_COLUMNS {
  label,
  startSettlementPeriod,
  endSettlementPeriod,
  date,
  status,
}

interface ISettlementOrderSearch {
  startSettlementPeriodId: string;
  endSettlementPeriodId: string;
}

const settlementOrderSearchInitialValues: ISettlementOrderSearch = {
  startSettlementPeriodId: '',
  endSettlementPeriodId: '',
};

export const settlementOrderAllInitialValues: ISettlementOrderLabelsForm = {
  settlementPeriodId: '',
  planedSettlementDate: formatDateToInput(new Date(), true),
  asap: true,
};

const settlementOrderByLabelInitialValues: ISettlementOrderByLabelForm = {
  labelId: '',
  startSettlementPeriodId: '',
  endSettlementPeriodId: '',
  planedSettlementDate: formatDateToInput(new Date(), true),
  asap: true,
};

export const settlementOrderByLabelValidator = object().shape({
  startSettlementPeriodId: string().required(
    i18next.t('formik-error.required')
  ),
  endSettlementPeriodId: string().required(i18next.t('formik-error.required')),
  planedSettlementDate: string().when('asap', {
    is: (asap: boolean) => !!asap,
    then: schema => schema.optional(),
    otherwise: schema => schema.required(i18next.t('formik-error.required')),
  }),
  asap: boolean(),
});

const settlementOrderLabelsValidator = object().shape({
  settlementPeriodId: string().required(i18next.t('formik-error.required')),
  planedSettlementDate: string().when('asap', {
    is: (asap: boolean) => !!asap,
    then: schema => schema.optional(),
    otherwise: schema => schema.required(i18next.t('formik-error.required')),
  }),
  asap: boolean(),
});

const settlementOrderByLabelFormValidator = settlementOrderByLabelValidator.shape({
  labelId: string().required(i18next.t('formik-error.required')),
});

const SettlementOrder = () => {
  usePermissionGuard('SettlementOrderPermissionsLIST**', '/', true, 'settlement-order');
  const hasPermission = useHasPermission();
  const queryClient = useQueryClient();

  const { t } = useTranslation();

  const [showWarningPopup, setShowWarningPopup] = useState(false);
  const [settlementData, setSettlementData] =
    useState<ISettlementOrderByLabelForm | null>(null);

  const checkPeriods = useCheckSettlementPeriods();

  const i18nextPrefix = 'settlement-order';

  const columnsVisibility = useTableColumnsVisibility(
    'SettlementOrder',
    SORT_COLUMNS
  );

  const searchById = useSearchById();
  const setSearchById = useSetSearchById();
  const filters = searchById[columnsVisibility.tableId] || settlementOrderSearchInitialValues;

  const settlementOrders = useGetSettlementOrderListInfinite(
    {
      page: 0,
      itemCount: 20,
    },
    filters.startSettlementPeriodId || undefined,
    filters.endSettlementPeriodId || undefined
  );

  const labelLightList = useGetLabelLightList();

  const settlementPeriods = useGetSettlementLightList({ controlApplicationSettings: true });

  const createSettlementOrder = useCreateSettlementOrder();

  const createSettlementOrderLabels = useGetSettlementOrderLabels();
  const cancelSettlementOrder = useCancelSettlementOrder();
  const cancelAllSettlementOrders = useCancelAllSettlementOrders();
  const acceptSettlementOrder = useAcceptSettlementOrder();
  const rejectSettlementOrder = useRejectSettlementOrder();

  const tableData = getNodes<ISettlementOrder>(
    settlementOrders.data?.pages.flat()
  );

  const { ref: filterRef, isComponentVisible: isFilterVisible, handleComponentVisible: handleFilterVisible } =
    useComponentVisible<HTMLDivElement>(false, true, columnsVisibility.tableId);
  
  const { ref: dropdownRef, isComponentVisible: isDropdownVisible, handleComponentVisible: handleDropdownVisible } =
    useComponentVisible<HTMLDivElement>(false);

  const settlementPeriodOptions =
    settlementPeriods.data?.flat().map(period => ({
      label: getSettlementPeriodName(period.settlementPeriodName),
      value: period.id,
    })) ?? [];

  const labelOptions =
    labelLightList.data?.map(label => ({
      label: label.labelName,
      value: label.id,
    })) ?? [];

  const buttonStyle = { justifyContent: 'start' };

  const onSubmitByLabel = async (
    values: ISettlementOrderByLabelForm,
    helpers: FormikHelpers<ISettlementOrderByLabelForm>,
    actions: PopupActions | null
  ) => {
    try {
      const checkResult = await checkPeriods.mutateAsync({
        labelId: values.labelId,
        startSettlementPeriodId: values.startSettlementPeriodId,
        endSettlementPeriodId: values.endSettlementPeriodId,
      });

      if (
        checkResult.hasOverlap ||
        checkResult.labelReportOverlap ||
        checkResult.hasGap
      ) {
        setSettlementData(values);
        setShowWarningPopup(true);
        actions?.close();
        return;
      }

      await createSettlementOrder.mutateAsync(mapCreateSettlementOrderBody(values), {
        onSuccess: async data => {
          await settlementOrders.refetch();
          actions?.close();
        },
        onError: error => onErrorShowAllErrorsToast(error),
      });
    } catch (error) {
      if (error instanceof Error) {
        onErrorShowAllErrorsToast(error);
      } else {
        onErrorShowAllErrorsToast(new Error('Unknown error occurred'));
      }
    }
  };

  const onSubmitAll = async (
    values: ISettlementOrderLabelsForm,
    helpers: FormikHelpers<ISettlementOrderLabelsForm>,
    actions: PopupActions | null
  ) => {
    await createSettlementOrderLabels.mutateAsync(
      mapCreateSettlementOrderLabelsBody(values),
      {
      onSuccess: async data => {
        await settlementOrders.refetch();
        actions?.close();
      },
      onError: async error => {
        onErrorShowAllErrorsToast(error);
        await settlementOrders.refetch();
      },
    }
    );
  };

  const handleCancelSettlementOrder = async (
    labelId: string,
    actions: PopupActions
  ) => {
    await cancelSettlementOrder.mutateAsync(
      { labelId },
      {
        onSuccess: async () => {
          await settlementOrders.refetch();
          actions.close();
        },
        onError: async error => {
          onErrorShowAllErrorsToast(error);
          await settlementOrders.refetch();
        },
      }
    );
  };

  const handleCancelAllSettlementOrders = async (actions: PopupActions) => {
    await cancelAllSettlementOrders.mutateAsync(
      {},
      {
        onSuccess: async () => {
          await settlementOrders.refetch();
          actions.close();
        },
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const handleAcceptSettlementOrder = async (settlementOrderId: string) => {
    await acceptSettlementOrder.mutateAsync(
      { settlementOrderId },
      {
        onSuccess: async () => {
          await settlementOrders.refetch();
          await queryClient.invalidateQueries({
            queryKey: ['settlement-requests-count'],
          });
        },
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const handleRejectSettlementOrder = async (settlementOrderId: string) => {
    await rejectSettlementOrder.mutateAsync(
      { settlementOrderId },
      {
        onSuccess: async () => {
          await settlementOrders.refetch();
          await queryClient.invalidateQueries({
            queryKey: ['settlement-requests-count'],
          });
        },
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const goToLabel = (id: string) => window.open(`/label/${id}/settlements`, '_blank');

  const getWarningMessage = () => {
    const messages = [];

    if (checkPeriods.data?.hasOverlap) {
      messages.push(t('settlement.warning.message.hasOverlap'));
    }

    if (checkPeriods.data?.labelReportOverlap) {
      messages.push(t('settlement.warning.message.labelReportOverlap'));
    }

    if (messages.length > 0) {
      return messages.join('\n\n');
    }

    if (checkPeriods.data?.hasGap) {
      return t('settlement.warning.message.hasGap');
    }

    return '';
  };

  const handleCreateSettlement = async () => {
    if (settlementData) {
      try {
        await createSettlementOrder.mutateAsync(
          mapCreateSettlementOrderBody(settlementData),
          {
          onSuccess: async data => {
            setShowWarningPopup(false);
            await settlementOrders.refetch();
          },
          onError: error => onErrorShowAllErrorsToast(error),
        }
        );
      } catch (createError) {
        if (createError instanceof Error) {
          onErrorShowAllErrorsToast(createError);
        } else {
          onErrorShowAllErrorsToast(
            new Error('Unknown error occurred while creating settlement')
          );
        }
      }
    }
  };

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`${i18nextPrefix}.title.list`)}
          hideBackButton
          topbarContent={
            <>
              <div className='flex'>
                <QueryFilter.ButtonOpener
                  i18nextPrefix={i18nextPrefix}
                  onClick={handleFilterVisible}
                  variant={BUTTON_VARIANT.default}
                  isClear
                />
                <Table.CustomColumnsButton
                  columns={SORT_COLUMNS}
                  isClear
                  visibility={columnsVisibility}
                  i18nextPrefix='input.label'
                  customI18next=''
                />
              </div>
              {!!tableData.nodes.find(
                order =>
                  order.settlementOrderStatusId ===
                  SETTLEMENT_ORDER_STATUS.waiting
              ) && (
                <MagellanPopups.CancelWarning
                  name={t('settlement-order.what-multiple')}
                  onCancel={handleCancelAllSettlementOrders}
                  trigger={
                    <Button variant={BUTTON_VARIANT.warning} onlyIcon isClear>
                      <Button.Description
                        description={t(`${i18nextPrefix}.cancelAllOrders`)}
                      />
                      <Button.Text
                        text={t(`${i18nextPrefix}.cancelAllOrders`)}
                      />
                      <Button.Icon icon={<PiXCircle />} />
                    </Button>
                  }
                />
              )}
              {hasPermission('SettlementOrderPermissionsLIST**', 1) && (
                <Button
                  variant={BUTTON_VARIANT.submit}
                  onClick={handleDropdownVisible}
                  outsideChildren={
                    <DropdownMenu
                      ref={dropdownRef}
                      visible={isDropdownVisible}
                      style={{ padding: 5 }}
                      justifyPosition={'end'}>
                      <div className='flex flex-col w-full'>
                        <MagellanPopups.Form
                          initialValues={settlementOrderByLabelInitialValues}
                          validationSchema={settlementOrderByLabelFormValidator}
                          onSubmit={onSubmitByLabel}
                          header={t('settlement-order.create.header')}
                          trigger={
                            <Button isClear buttonStyle={buttonStyle} fullWidth>
                              <Button.Text
                                text={t(`${i18nextPrefix}.create.orderByLabel`)}
                              />
                            </Button>
                          }>
                          {({ values }) => (
                            <>
                              <FormInput.Select
                                name='labelId'
                                label={t('input.label.label')}
                                options={labelOptions}
                                isAsync
                              />
                              <FormInput.Select
                                name='startSettlementPeriodId'
                                label={t('input.label.startDate')}
                                options={settlementPeriodOptions}
                                isAsync
                              />
                              <FormInput.Select
                                name='endSettlementPeriodId'
                                label={t('input.label.endDate')}
                                options={settlementPeriodOptions}
                                isAsync
                              />
                              <FormInput.Select
                                name={`asap`}
                                label={t('input.label.asap')}
                                options={[
                                  { label: t('input.option.yes'), value: true },
                                  { label: t('input.option.no'), value: false },
                                ]}
                              />
                              <FormInput.Text
                                label={t('input.label.plannedStartDate')}
                                type='datetime-local'
                                min={formatDateToInput(new Date(), true)}
                                name='planedSettlementDate'
                                disabled={values.asap !== false}
                              />
                            </>
                          )}
                        </MagellanPopups.Form>
                        <MagellanPopups.Form
                          initialValues={settlementOrderAllInitialValues}
                          validationSchema={settlementOrderLabelsValidator}
                          onSubmit={onSubmitAll}
                          header={t('settlement-order.create.headerMultiple')}
                          trigger={
                            <Button isClear buttonStyle={buttonStyle} fullWidth>
                              <Button.Text
                                text={t(`${i18nextPrefix}.create.orderAll`)}
                              />
                            </Button>
                          }>
                          {({ values }) => (
                            <>
                              <FormInput.Select
                                name='settlementPeriodId'
                                label={t('input.label.settlementPeriodId')}
                                options={settlementPeriodOptions}
                                isAsync
                              />
                              <FormInput.Select
                                name={`asap`}
                                label={t('input.label.asap')}
                                options={[
                                  { label: t('input.option.yes'), value: true },
                                  { label: t('input.option.no'), value: false },
                                ]}
                              />
                              <FormInput.Text
                                label={t('input.label.plannedStartDate')}
                                type='datetime-local'
                                min={formatDateToInput(new Date(), true)}
                                name='planedSettlementDate'
                                disabled={values.asap !== false}
                              />
                            </>
                          )}
                        </MagellanPopups.Form>
                      </div>
                    </DropdownMenu>
                  }>
                  <Button.Text text={t(`${i18nextPrefix}.addOrder`)} />
                </Button>
              )}
            </>
          }>
          <QueryFilter<ISettlementOrderSearch>
            i18nextPrefix={i18nextPrefix}
            initialValues={filters}>
            <>
              <QueryFilter.Form<ISettlementOrderSearch>
                ref={filterRef}
                isOpen={isFilterVisible}
                onDebouncedFormChange={values => setSearchById(columnsVisibility.tableId, values)}>
                <QueryFilter.InputsGrid>
                  <FormInput.Select
                    name='startSettlementPeriodId'
                    label={t('input.label.startDate')}
                    options={settlementPeriodOptions}
                    isAsync
                  />
                  <FormInput.Select
                    name='endSettlementPeriodId'
                    label={t('input.label.endDate')}
                    options={settlementPeriodOptions}
                    isAsync
                  />
                </QueryFilter.InputsGrid>
              </QueryFilter.Form>
              <QueryFilter.Labels
                isFormOpen={isFilterVisible}
                customLabel={data => {
                  const period = settlementPeriods.data?.flat().find(p => p.id === data.value);
                  return period ? getSettlementPeriodName(period.settlementPeriodName) : `${data.value}`;
                }}
              />
            </>
          </QueryFilter>
        </TopBar>
      }>
      <Table
        id={columnsVisibility.tableId}
        loading={
          settlementOrders.isLoading || settlementOrders.isFetchingNextPage
        }
        onFullyScroll={settlementOrders.fetchNextPage}
        data={tableData}
        size='compact'
        columns={[
          {
            label: t('input.label.label'),
            renderCell: (item: ISettlementOrder) => (
              <TableItem>
                <TableItem.Text text={item.label?.labelName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.label),
          },
          {
            label: t('input.label.startSettlementPeriod'),
            renderCell: item => (
              <TableItem>
                <TableItem.Text
                  text={getSettlementPeriodName(item.startSettlementPeriod)}
                />
                <TableItem.Text
                  isSupplementary
                  text={
                    item.startSettlementPeriod.startDate &&
                    formatDate(item.startSettlementPeriod.startDate)
                  }
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.startSettlementPeriod),
          },

          {
            label: t('input.label.endSettlementPeriod'),
            renderCell: (item: ISettlementOrder) => (
              <TableItem>
                {item.endSettlementPeriod.name && (
                  <TableItem.Text
                    text={getSettlementPeriodName(item.endSettlementPeriod)}
                  />
                )}
                {!!item.endSettlementPeriod.name && (
                  <TableItem.Text
                    isSupplementary
                    text={
                      item.endSettlementPeriod.endDate
                        ? formatDate(item.endSettlementPeriod.endDate)
                        : undefined
                    }
                  />
                )}
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.endSettlementPeriod),
          },

          {
            label: t('input.label.date'),
            renderCell: (item: ISettlementOrder) => (
              <TableItem>
                <TableItem.Text text={formatDate(item.settlementDate, true)} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(SORT_COLUMNS.date),
          },
          {
            label: t('input.label.status'),
            renderCell: (item: ISettlementOrder) => (
              <TableItem>
                <LabeledSettlementOrderStatus
                  status={item.settlementOrderStatusId}
                />
              </TableItem>
            ),
            hide: columnsVisibility.hide(SORT_COLUMNS.status),
          },
          {
            label: '',
            renderCell: (item: ISettlementOrder) => (
              <TableItem justifyPosition='end'>
                <div className='flex gap-2'>
                  {item.settlementOrderStatusId ===
                    SETTLEMENT_ORDER_STATUS.waiting && (
                    <MagellanPopups.CancelWarning
                      name={t('settlement-order.what')}
                      onCancel={actions =>
                        item.label &&
                        handleCancelSettlementOrder(item.label.id, actions)
                      }
                    />
                  )}
                  {item.settlementOrderStatusId ===
                    SETTLEMENT_ORDER_STATUS.request && (
                    <>
                      <Button
                        isClear
                        onlyIcon
                        variant={BUTTON_VARIANT.submit}
                        disabled={
                          (acceptSettlementOrder.isPending &&
                            acceptSettlementOrder.variables?.settlementOrderId === item.id) ||
                          (rejectSettlementOrder.isPending &&
                            rejectSettlementOrder.variables?.settlementOrderId === item.id)
                        }
                        isLoading={
                          acceptSettlementOrder.isPending &&
                          acceptSettlementOrder.variables?.settlementOrderId === item.id
                        }
                        onClick={() => handleAcceptSettlementOrder(item.id)}>
                        <Button.Description description={t('button.accept')} />
                        <Button.Icon icon={<PiCheck />} />
                      </Button>
                      <Button
                        isClear
                        onlyIcon
                        disabled={
                          (rejectSettlementOrder.isPending &&
                            rejectSettlementOrder.variables?.settlementOrderId === item.id) ||
                          (acceptSettlementOrder.isPending &&
                            acceptSettlementOrder.variables?.settlementOrderId === item.id)
                        }
                        isLoading={
                          rejectSettlementOrder.isPending &&
                          rejectSettlementOrder.variables?.settlementOrderId === item.id
                        }
                        onClick={() => handleRejectSettlementOrder(item.id)}>
                        <Button.Description description={t('button.cancel')} />

                        <Button.Icon icon={<PiX />} />
                      </Button>
                    </>
                  )}
                  <Button
                    isClear
                    onlyIcon
                    onClick={() => item.label && goToLabel(item.label.id)}>
                    <Button.Description description={t('button.open')} />

                    <Button.Icon
                      icon={<PiNavigationArrow transform='scale(-1,1)' />}
                    />
                  </Button>
                </div>
              </TableItem>
            ),
          },
        ]}
      />
      <MagellanPopups.WarningPopup
        open={showWarningPopup}
        onClose={() => setShowWarningPopup(false)}
        onConfirm={
          checkPeriods.data?.hasOverlap || checkPeriods.data?.labelReportOverlap
            ? undefined
            : handleCreateSettlement
        }
        title={t('settlement.warning.title')}
        message={getWarningMessage()}
      />
    </TopbarPage>
  );
};

export default SettlementOrder;
